import React from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';

interface ModuleSection {
  title: string;
  content: string[];
  illustrationText?: string;
  additionalSection?: {
    title: string;
    content: string[];
  };
  additionalSection2?: {
    title: string;
    content: string[];
  };
}

// Define the content for the multi-step text module
const moduleContent: ModuleSection[] = [
  {
    title: 'Why This Practice Changes Everything',
    content: [
      'Mindfulness isn\'t just about sitting still; it\'s about changing your relationship with your own mind and feelings.'
    ],
    additionalSection: {
      title: 'Getting Smart About Your Feelings',
      content: [
        'The goal is to build self-awareness. Instead of being controlled by a sudden wave of anger or anxiety, you learn to notice it. You create a tiny space between the feeling and your reaction. You realize: "I am not this anger; a feeling of anger is simply here right now." This ability to see your emotions clearly is what gives you emotional freedom and control.',
      ]
    },
    additionalSection2: {
      title: 'Your Gateway to Peace and Gratitude',
      content: [
        'The moment you settle into the present, two beautiful things happen:',
        '• <strong>You find Peace:</strong> Your mind stops running frantic scenarios about the future or rehashing the past. When your attention is <strong>anchored to the present</strong> (like watching your breath), the mental chatter quiets down, and you access a <strong>natural state of calm</strong>.',
        '• <strong>You unlock Gratitude:</strong> When you pay attention, you stop taking the little things for granted. The simple fact that you can breathe, see, or walk becomes a <strong>miracle</strong>. Presence shifts your focus from what\'s missing to what is <strong>abundant right now</strong>.',
      ]
    }
  },
  {
    title: 'Contextualizing the Practices',
    content: [
      'The chosen practices are designed to integrate the spiritual philosophy into daily life, turning routine into ritual.',
      '• <strong>Breathing Awareness:</strong> This isn\'t just relaxation; it\'s a profound spiritual tool. The breath is the <strong>ever-present reality</strong> and the constant connection to life. Observing it is a direct, immediate grounding in the sacred "now."',
      '• <strong>Mindful Transitions:</strong> This practice combats the feeling that life is mechanical or lived on autopilot. By pausing between tasks (e.g., stopping at the doorway, taking a conscious breath), clients deliberately inject <strong>intention</strong> and <strong>awareness</strong> back into their routine, turning habitual actions into mindful rituals.',
      '• <strong>"What lesson did today bring?":</strong> This isn\'t just a simple question; it\'s a powerful little switch. It instantly stops you from just drifting through life and puts you in the driver\'s seat of your own growth. Instead of letting experiences wash over you (which is the passive part), it forces you to actively look for the learning.',
      'It\'s the key that unlocks spiritual growth because it helps you see all of life–even the frustrating or messy parts–not as problems, but as teaching moments. It turns the daily routine into a deep, meaningful contemplation.',
      '<br/>',
      '<strong>Key Message:</strong> Connection grows when we see others not as separate, but as mirrors of our shared humanity.'
    ],
  },
  {
    title: 'Ready to Test Your Knowledge?',
    content: [
      'You\'ve completed the module content! Now, let\'s reinforce your learning with a quick quiz to check your understanding.'
    ],
    additionalSection: {
      title: 'Next Steps',
      content: [
        'Click "Take the Quiz" below to test your knowledge about mindfulness and cultivating presence.',
        'The quiz will help you review key concepts about being present in the moment, mindful practices, and finding peace through awareness.'
      ]
    }
  },
];

// Main component, matching the design of the standard assessment form
const SpiritualModule3Page = () => {
  const navigate = useNavigate();
  const { currentStep: rawCurrentStep, setCurrentStep, isLoading, saveProgress } = useModuleProgress('spiritualM3');
  const totalSteps = moduleContent.length;

  // Ensure currentStep is within valid bounds
  const currentStep = Math.min(rawCurrentStep, totalSteps - 1);

  const currentModule = moduleContent[currentStep];
  const progress = ((currentStep + 1) / totalSteps) * 100;

  const handleNext = async () => {
    if (currentStep < totalSteps - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      await saveProgress(nextStep);
    } else {
      // Mark the module as completed and navigate to quiz
      await saveProgress(currentStep, true);
      navigate('/panel/learn/spiritual-module-3/quiz');
    }
  };

  const handleBack = async () => {
    if (currentStep === 0) {
      navigate('/panel/learn/spiritual-resources');
      return;
    }
    const prevStep = currentStep - 1;
    setCurrentStep(prevStep);
    await saveProgress(prevStep);
  };

  const isCompleted = currentStep === totalSteps - 1;

  return (
    // 1. Updated wrapper class: module-form-page -> module-form
    <div className="module-form">
      <div className="module-container">
        
        {/* Left Panel - Illustration */}
        {/* 2. Updated left panel class: module-left-panel -> module-left */}
        <div 
          className="module-left spiritual-panel"
        >
          {/* 3. Retaining module-header for left panel content */}
          <div className="module-header">
            <h1>Module 3</h1>
            <p className="module-subtitle">Mindfulness – Cultivating Presence and Awareness</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
            Mindfulness is simply the practice of being <strong>here, now</strong>. It's the opposite of living on autopilot, where you drive to work and can't remember the trip, or you eat a meal without tasting it. 
            Spiritually, this practice is so powerful because it treats the <strong>present moment as sacred</strong>. It helps you stop rushing past your life and start <strong>fully experiencing</strong> it.
           </p> 
           <p className="module-tool-info">
            When you're constantly rushing, worrying, or distracted, life feels mechanical and dull. Mindfulness is the tool that lets you <strong>stop the machinery</strong> and rediscover the <strong>deep meaning in ordinary moments</strong>–like the warmth of a morning coffee or the sound of rain.
            </p>
          </div>
        </div>

        {/* Right Panel - Content */}
        {/* 4. Updated right panel class: module-right-panel -> module-right */}
        <div className="module-right">
          
          {/* Content Wrapper for flex space-between layout */}
          <div className="module-content"> 
          
            {/* Progress Bar */}
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Page {currentStep + 1} of {totalSteps}
              </span>
            </div>

            {/* Content Section */}
            <div className="module-section">
              <h2 className="module-title"> 
                {` ${currentModule.title}`}
              </h2>
              <div className="content-section">
                {currentModule.content.map((paragraph, index) => (
                  <p key={index} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                ))}
              </div>
            </div>

            {/* Additional Title Sections */}
            {currentModule.additionalSection && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection.content.map((paragraph: string, index: number) => (
                    <p key={`additional-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
            {currentModule.additionalSection2 && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection2.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection2.content.map((paragraph: string, index: number) => (
                    <p key={`additional2-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Navigation Buttons */}
          <div className="navigation-buttons">
            <button
              className="btn-secondary"
              onClick={handleBack}
              // Logic moved to handleBack, so button is never disabled until the end state is reached.
              disabled={false} 
            >
              {currentStep === 0 ? 'Exit Module' : 'Back'}
            </button>
            
            <button
              className="btn-primary"
              onClick={handleNext}
            >
              {isCompleted ? 'Take the Quiz' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpiritualModule3Page;

